import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Trophy, AlertTriangle, Shield, Eye, Minus,
  Filter, ArrowUpDown, ChevronUp, ChevronDown,
  TrendingUp, TrendingDown, Info,
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import DateFilter from "../components/DateFilter";
import { SyncDataButton } from "../components/SyncDataButton";
import { CopyButton } from "../components/CopyField";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ORDER = { Elite: 0, Good: 1, Monitor: 2, "High Risk": 3, "Low Data": 4 };

const STATUS_CFG = {
  Elite:       { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/20", dot: "bg-purple-400", icon: Trophy },
  Good:        { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20", dot: "bg-emerald-400", icon: Shield },
  Monitor:     { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20", dot: "bg-amber-400", icon: Eye },
  "High Risk": { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20", dot: "bg-red-400", icon: AlertTriangle },
  "Low Data":  { bg: "bg-slate-500/10", text: "text-slate-400", border: "border-slate-500/20", dot: "bg-slate-400", icon: Minus },
};

const getScoreColor = (score) => {
  if (score >= 85) return "text-purple-400";
  if (score >= 70) return "text-emerald-400";
  if (score >= 50) return "text-amber-400";
  return "text-red-400";
};

const getScoreBarColor = (score) => {
  if (score >= 85) return "bg-purple-500";
  if (score >= 70) return "bg-emerald-500";
  if (score >= 50) return "bg-amber-500";
  return "bg-red-500";
};

const ScoreBreakdown = ({ breakdown, stats }) => {
  if (!breakdown) return null;
  const pos = breakdown.positive;
  const neg = breakdown.negative;
  return (
    <div className="mt-2 p-3 bg-slate-950/60 border border-slate-800/40 rounded-lg text-xs space-y-2">
      <div className="flex items-center gap-1.5 text-emerald-400 font-semibold mb-1">
        <TrendingUp className="h-3 w-3" /> Positive ({breakdown.positive_total}/80)
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
        <span>Valid Call Rate</span><span className="text-right text-emerald-400">+{pos.valid_call_rate}</span>
        <span>Conversion Rate</span><span className="text-right text-emerald-400">+{pos.conversion_rate}</span>
        <span>Call Duration</span><span className="text-right text-emerald-400">+{pos.call_duration}</span>
        <span>Consistency</span><span className="text-right text-emerald-400">+{pos.consistency}</span>
      </div>
      <div className="flex items-center gap-1.5 text-red-400 font-semibold mt-2 mb-1">
        <TrendingDown className="h-3 w-3" /> Negative (-{breakdown.negative_total}/30)
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
        <span>Fake Call Rate</span><span className="text-right text-red-400">-{neg.fake_call_rate}</span>
        <span>Adult Calls</span><span className="text-right text-red-400">-{neg.adult_calls}</span>
        <span>IVR/Robot</span><span className="text-right text-red-400">-{neg.ivr_robot}</span>
        <span>Non-US</span><span className="text-right text-red-400">-{neg.non_us}</span>
        <span>Buy/Sell</span><span className="text-right text-red-400">-{neg.buy_sell}</span>
      </div>
      {stats && (
        <div className="mt-2 pt-2 border-t border-slate-800/40 grid grid-cols-3 gap-x-3 gap-y-1 text-slate-500">
          <span>Valid: {stats.valid_calls}</span>
          <span>Fake: {stats.fake_calls}</span>
          <span>Adult: {stats.adult_calls}</span>
          <span>IVR: {stats.ivr_calls}</span>
          <span>Non-US: {stats.non_us_calls}</span>
          <span>Avg Dur: {Math.floor(stats.avg_duration / 60)}:{String(stats.avg_duration % 60).padStart(2, "0")}</span>
        </div>
      )}
    </div>
  );
};

export default function PublisherRankingPage() {
  const [publishers, setPublishers] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ Elite: 0, Good: 0, Monitor: 0, "High Risk": 0, "Low Data": 0 });
  const [pubNames, setPubNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [publisherFilter, setPublisherFilter] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [ringbaUnavailable, setRingbaUnavailable] = useState(false);
  const intervalRef = useRef(null);

  const fetchNames = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/publisher-control/names`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPubNames(res.data.publishers || []);
    } catch (err) {
      console.error("Failed to fetch publisher names:", err);
    }
  }, []);

  useEffect(() => { fetchNames(); }, [fetchNames]);

  const fetchData = useCallback(async (silent = false) => {
    if (!dateFilter) return;
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("start_date", dateFilter.startDate);
      params.set("end_date", dateFilter.endDate);
      if (publisherFilter) params.set("publisher", publisherFilter);

      const res = await axios.get(`${API}/api/publisher-ranking/scores?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPublishers(res.data.publishers || []);
      setStatusCounts(res.data.status_counts || { Elite: 0, Good: 0, Monitor: 0, "High Risk": 0, "Low Data": 0 });
      setRingbaUnavailable(!!res.data.ringba_unavailable);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Ranking fetch error:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateFilter, publisherFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!dateFilter) return;
    intervalRef.current = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [dateFilter, publisherFilter, fetchData]);

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(col === "publisher_name"); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/publisher-control/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await Promise.all([fetchNames(), fetchData(true)]);
      toast.success("Ranking data synced");
    } catch (err) {
      toast.error("Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const sorted = sortCol
    ? [...publishers].sort((a, b) => {
        if (sortCol === "status") {
          const so = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
          return sortAsc ? so : -so;
        }
        const av = a[sortCol] ?? 0;
        const bv = b[sortCol] ?? 0;
        if (typeof av === "string") return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortAsc ? av - bv : bv - av;
      })
    : publishers;

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 text-slate-600" />;
    return sortAsc ? <ChevronUp className="h-3 w-3 text-blue-400" /> : <ChevronDown className="h-3 w-3 text-blue-400" />;
  };

  return (
    <div className="space-y-6" data-testid="publisher-ranking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>Publisher Ranking</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Weighted score: Valid Rate, Conversions, Duration, Consistency vs Fake, Adult, IVR, Non-US, Buy/Sell
            {lastUpdated && <span className="ml-1 sm:ml-2 text-slate-600" data-testid="last-updated-ranking">&middot; Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800/50 rounded-lg px-3 py-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-500" />
            <select
              value={publisherFilter}
              onChange={(e) => setPublisherFilter(e.target.value)}
              className="bg-transparent text-sm text-slate-300 outline-none cursor-pointer max-w-[180px]"
              data-testid="ranking-publisher-filter"
            >
              <option value="" className="bg-slate-900">All Publishers</option>
              {pubNames.map((n) => (
                <option key={n} value={n} className="bg-slate-900">{n}</option>
              ))}
            </select>
          </div>
          <SyncDataButton onSync={handleSync} syncing={syncing} lastSynced={lastUpdated} ringbaUnavailable={ringbaUnavailable} testId="sync-data-btn" />
        </div>
      </div>

      {/* Date Filter */}
      <DateFilter onFilterChange={setDateFilter} defaultPreset="last_30_days" />

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {/* Status Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(STATUS_CFG).map(([status, cfg]) => {
              const Icon = cfg.icon;
              return (
                <div
                  key={status}
                  className={cn("rounded-xl border p-4 flex items-center gap-3", cfg.bg, cfg.border)}
                  data-testid={`status-card-${status.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className={cn("h-5 w-5", cfg.text)} />
                  <div>
                    <p className={cn("text-xl font-bold", cfg.text)} style={{ fontFamily: "Outfit, sans-serif" }}>
                      {statusCounts[status] || 0}
                    </p>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{status}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Ranking Table */}
          <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl overflow-hidden" data-testid="ranking-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800/50">
                    {[
                      { key: "publisher_name", label: "Publisher Name" },
                      { key: "total_calls", label: "Total Calls" },
                      { key: "score", label: "Score" },
                      { key: "status", label: "Status" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-300 select-none"
                        onClick={() => handleSort(col.key)}
                      >
                        <span className="flex items-center gap-1.5">{col.label} <SortIcon col={col.key} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">No publisher data for the selected range</td></tr>
                  ) : (
                    sorted.map((pub, i) => {
                      const cfg = STATUS_CFG[pub.status] || STATUS_CFG["Low Data"];
                      const Icon = cfg.icon;
                      const isExpanded = expandedRow === pub.publisher_name;
                      return (
                        <tr
                          key={pub.publisher_name}
                          className={cn(
                            "border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors cursor-pointer",
                            isExpanded && "bg-slate-800/20"
                          )}
                          data-testid={`ranking-row-${i}`}
                          onClick={() => setExpandedRow(isExpanded ? null : pub.publisher_name)}
                        >
                          <td className="px-4 py-3 font-medium text-slate-200">
                            <div className="group flex items-center gap-2">
                              {pub.publisher_name}
                              <CopyButton text={pub.publisher_name} label="publisher name" testId={`copy-name-${i}`} />
                              <Info className="h-3 w-3 text-slate-600" />
                            </div>
                            {isExpanded && (
                              <ScoreBreakdown breakdown={pub.breakdown} stats={pub.stats} />
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-300 tabular-nums align-top">{pub.total_calls.toLocaleString()}</td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-slate-800 rounded-full h-1.5">
                                <div
                                  className={cn("h-1.5 rounded-full transition-all", getScoreBarColor(pub.score))}
                                  style={{ width: `${pub.score}%` }}
                                />
                              </div>
                              <span className={cn("font-bold tabular-nums", getScoreColor(pub.score))} data-testid={`score-${i}`}>
                                {pub.score}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border", cfg.bg, cfg.text, cfg.border)}>
                              <Icon className="h-3 w-3" />
                              {pub.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
